import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { buildFunctionUrl } from '../utils/api-config';

// Types for API requests and responses
interface CreateSessionData {
  assessmentId: string;
  // Participant info is optional for anonymous sessions
  participantName?: string;
  participantEmail?: string;
  participantPhone?: string;
  participantOrganization?: string;
  participantPosition?: string;
  participantAddress?: string;
  participantEmployeeCount?: string;
}

interface ParticipantInfo {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  position?: string;
  address?: string;
  employeeCount?: string;
}

interface SubmitResponsesData {
  sessionId: string;
  responses: Array<{
    questionId: string;
    answer: string;
    questionText?: string;
  }>;
  // Participant info can be submitted with responses (for post-questionnaire collection)
  participantInfo?: ParticipantInfo;
}

// Assessment API - Public functions only
export const assessmentAPI = {
  // Get all assessments using Firebase Function
  getAll: async (language?: 'he' | 'en') => {
    const getAllAssessmentsFunction = httpsCallable(functions, 'getAllAssessments');
    const result = await getAllAssessmentsFunction({ language, isActive: true });
    return { data: result.data };
  },

  // Get assessment by ID using Firebase Function
  getById: async (id: string) => {
    const getAssessmentByIdFunction = httpsCallable(functions, 'getAssessmentById');
    const result = await getAssessmentByIdFunction({ id });
    return { data: result.data };
  },

  // Get assessments by type using Firebase Function
  getByType: async (type: '360', language?: 'he' | 'en') => {
    const getAssessmentsByTypeFunction = httpsCallable(functions, 'getAssessmentsByType');
    const result = await getAssessmentsByTypeFunction({ type, language: language || 'he', isActive: true });
    return { data: result.data };
  },

  // Initialize assessments
  initialize: async () => {
    const response = await fetch(buildFunctionUrl('initializeAssessments'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result };
  },
};

// Session API - Public functions only
export const sessionAPI = {
  // Create session
  create: async (data: CreateSessionData) => {
    const createSessionFunction = httpsCallable(functions, 'createSession');
    const result = await createSessionFunction(data);
    return { data: (result.data as any).session };
  },

  // Get session by ID using Firebase Function
  getById: async (id: string) => {
    const getSessionByIdFunction = httpsCallable(functions, 'getSessionById');
    const result = await getSessionByIdFunction({ id });
    return { data: result.data };
  },

  // Submit responses
  submitResponses: async (id: string, data: Omit<SubmitResponsesData, 'sessionId'>) => {
    const submitData: SubmitResponsesData = { ...data, sessionId: id };
    const submitResponsesFunction = httpsCallable(functions, 'submitResponses');
    const result = await submitResponsesFunction(submitData);
    return { data: (result.data as any).session };
  },

  // Get session progress
  getProgress: async (id: string) => {
    const response = await fetch(buildFunctionUrl(`getSessionProgress/${id}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result };
  },

  // Get session responses
  getResponses: async (id: string, page?: number) => {
    const url = new URL(buildFunctionUrl(`getSessionResponses/${id}`));
    if (page) {
      url.searchParams.append('page', page.toString());
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return { data: result.responses };
  },
};

// Secure invite API - Public functions
export const inviteAPI = {
  // Validate invite token (public)
  validateInvite: async (companyId: string, assessmentId: string, inviteId: string, token: string) => {
    const validateInviteFunction = httpsCallable(functions, 'validateInvite');
    const result = await validateInviteFunction({ companyId, assessmentId, inviteId, token });
    return result.data;
  },

  // Submit with token (public)
  submitWithToken: async (companyId: string, assessmentId: string, inviteId: string, token: string, responses: any, participantInfo?: any) => {
    const submitWithTokenFunction = httpsCallable(functions, 'submitWithToken');
    const result = await submitWithTokenFunction({ companyId, assessmentId, inviteId, token, responses, participantInfo });
    return result.data;
  },
};

// Error handling wrapper
const handleFirebaseError = (error: any) => {
  console.error('Firebase API Error:', error);
  
  if (error.code) {
    switch (error.code) {
      case 'functions/not-found':
        throw new Error('The requested function was not found');
      case 'functions/permission-denied':
        throw new Error('Permission denied');
      case 'functions/unauthenticated':
        throw new Error('Authentication required');
      case 'functions/resource-exhausted':
        throw new Error('Service temporarily unavailable');
      case 'functions/internal':
        throw new Error('Internal server error');
      default:
        throw new Error(error.message || 'An unknown error occurred');
    }
  }
  
  throw error;
};

// Wrap all API calls with error handling
const createErrorWrapper = (apiObj: any) => {
  const wrapped: any = {};
  for (const key in apiObj) {
    if (typeof apiObj[key] === 'function') {
      wrapped[key] = async (...args: any[]) => {
        try {
          return await apiObj[key](...args);
        } catch (error) {
          handleFirebaseError(error);
        }
      };
    } else if (typeof apiObj[key] === 'object') {
      wrapped[key] = createErrorWrapper(apiObj[key]);
    } else {
      wrapped[key] = apiObj[key];
    }
  }
  return wrapped;
};

// Export wrapped APIs
export const wrappedAssessmentAPI = createErrorWrapper(assessmentAPI);
export const wrappedSessionAPI = createErrorWrapper(sessionAPI);
export const wrappedInviteAPI = createErrorWrapper(inviteAPI);

// Default exports for drop-in replacement
const publicAPIDefault = {
  assessmentAPI: wrappedAssessmentAPI,
  sessionAPI: wrappedSessionAPI,
  inviteAPI: wrappedInviteAPI,
};

export default publicAPIDefault;